/**
 * Which drums are ONE physical bobbin shared by several products.
 *
 * A merge-execution extrudes two products onto one drum in a single pass, and a
 * partial fork cuts one drum between two products — both end with the SAME
 * tokenId under more than one job, because it is the same object on the floor.
 *
 * Nothing on a row says so. Every screen that lists drums per product therefore
 * shows the same bobbin twice, in two places, looking like two drums — and the
 * only way to notice was to read token ids down the table and spot a repeat.
 * That is a memory game, not a display.
 *
 * Sharing is decided by JOB, not by product label: two separate lots of
 * "CYYF 3x2.5" are two different products with the same name, and calling their
 * drums shared would invent a merge that never happened. The label is only what
 * gets printed once sharing is established.
 *
 * Pure, and shape-agnostic on purpose — callers flatten their own rows into
 * `{ tokenId, jobId, productLabel }` triples, because the queue, the token table
 * and the 3D panel each hold them differently.
 */

const str = v => (v === null || v === undefined || v === '' ? null : String(v));

/**
 * @param {{tokenId?: any, jobId?: any, productLabel?: any}[]} entries
 * @returns {Map<string, { jobIds: string[], labels: string[] }>}
 *          only tokens genuinely held by more than one job
 */
export function buildSharedTokenIndex(entries) {
  const seen = new Map();

  for (const e of Array.isArray(entries) ? entries : []) {
    const tokenId = str(e?.tokenId);
    const jobId = str(e?.jobId);
    // A drum with no job cannot be shown as shared WITH anybody — phantom stock
    // rows carry no jobId, and lumping them together by a null key would weld
    // unrelated drums into one fictitious merge.
    if (!tokenId || !jobId) continue;

    if (!seen.has(tokenId)) seen.set(tokenId, new Map());
    const byJob = seen.get(tokenId);
    // first label wins — later rows for the same job are the same product
    if (!byJob.has(jobId)) byJob.set(jobId, str(e?.productLabel) || jobId);
  }

  const shared = new Map();
  for (const [tokenId, byJob] of seen) {
    // One job listing the same drum twice (a produced row and its plan-ahead
    // twin) is not sharing. Keying by job makes that free.
    if (byJob.size < 2) continue;
    shared.set(tokenId, {
      jobIds: [...byJob.keys()],
      labels: [...byJob.values()]
    });
  }

  return shared;
}

/**
 * The OTHER products holding this drum — what a row should name.
 *
 * Empty when the drum is not shared, or when the caller is the only holder.
 */
export function sharedPartners(index, tokenId, jobId) {
  const hit = index instanceof Map ? index.get(str(tokenId)) : null;
  if (!hit) return [];

  const mine = str(jobId);
  const out = [];
  hit.jobIds.forEach((id, i) => {
    if (id === mine) return;
    const label = hit.labels[i];
    if (label && !out.includes(label)) out.push(label);
  });
  return out;
}

/** Is this drum one bobbin under more than one product? */
export function isSharedToken(index, tokenId) {
  return index instanceof Map && index.has(str(tokenId));
}

const n0 = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * How a shared drum divides between the products on it.
 *
 * `sharedPartners` answers "who else", which is enough for a table cell. A
 * picture of the floor needs the split too: one bobbin carrying 2 000 m of
 * N2XH 4x1.5 and 5 300 m of N2XH 5x1.5 is a merge run, and showing the two
 * products without their metres makes it look like a duplicate rather than a
 * shared lot.
 *
 * Shares come from the per-(job, token) rows the caller already has — each job's
 * row carries ITS length on that drum, which is exactly the split.
 *
 * @param entries `{ tokenId, jobId, productLabel, availableLength?, producedLength? }`
 * @returns Map tokenId → [{ jobId, label, available, produced }], biggest first,
 *          only for drums genuinely held by more than one job
 */
/**
 * One openable group per shared drum, holding EVERY product on it.
 *
 * The 3D hall draws a shared bobbin once, so the products on it used to float a
 * card each at the same spot — a card over a card, and neither said they were
 * the same object. Now the drum carries one card, and this is what that card
 * opens: a group whose tokens span all the holding jobs.
 *
 * It needs no special case downstream. The detail panel already plans from
 * (jobId, tokenId) pairs, so a group spanning two jobs carries both products
 * into the wizard by construction, rather than one product at a time.
 *
 * @param entries the same `{ tokenId, jobId, productLabel, ... }` rows the
 *                indexes above take — one per (job, token)
 * @returns Map tokenId → { merged, phase, product, jobId, mergeShares, tokens }
 */
export function buildMergeGroups(entries) {
  const shares = sharedShareIndex(entries);
  const partners = buildSharedTokenIndex(entries);
  const out = new Map();

  for (const e of Array.isArray(entries) ? entries : []) {
    const tokenId = str(e?.tokenId);
    if (!tokenId || !shares.has(tokenId)) continue;

    const g = out.get(tokenId) || { merged: true, phase: e?.phase ?? null, tokens: [] };
    g.tokens.push({ ...e, sharedWith: sharedPartners(partners, e.tokenId, e.jobId) });
    out.set(tokenId, g);
  }

  for (const [tokenId, g] of out) {
    const list = shares.get(tokenId);
    g.mergeShares = list;
    // Biggest share first, so the product that owns most of the drum reads
    // first — the same order the card prints.
    g.product = list.map(s => s.label).join('  +  ');
    g.jobId = g.tokens[0]?.jobId ?? null;
  }

  return out;
}

export function sharedShareIndex(entries) {
  const byToken = new Map();

  for (const e of Array.isArray(entries) ? entries : []) {
    const tokenId = str(e?.tokenId);
    const jobId = str(e?.jobId);
    if (!tokenId || !jobId) continue;

    if (!byToken.has(tokenId)) byToken.set(tokenId, new Map());
    const byJob = byToken.get(tokenId);
    // A job listing the same drum twice (a produced row and its plan-ahead
    // twin) is one holder, and the first row is the one that counts — same rule
    // buildSharedTokenIndex uses, so the two indexes cannot disagree about who
    // holds what.
    if (byJob.has(jobId)) continue;
    byJob.set(jobId, {
      jobId,
      label: str(e?.productLabel) || jobId,
      available: n0(e?.availableLength),
      produced: n0(e?.producedLength),
    });
  }

  const out = new Map();
  for (const [tokenId, byJob] of byToken) {
    if (byJob.size < 2) continue;
    out.set(
      tokenId,
      [...byJob.values()].sort((a, b) => (b.available || b.produced) - (a.available || a.produced))
    );
  }
  return out;
}
